/**
 * Pricing model — Stripe-style Packages + Prices, plus Coupons + Add-ons.
 * Field names mirror NESTJS_SCHEMA.md tables.
 *
 * Package tiers + monthly amounts mirror the production possignin.com pricing
 * page exactly. Yearly prices are 12× monthly (no annual discount, per
 * product spec).
 */

export type Package = {
  id: string;
  internalCode: string;
  name: string;
  tagline?: string;
  status: 'draft' | 'published' | 'deprecated' | 'archived';
  visibility: 'public' | 'private' | 'internal';
  soldBy: 'all' | 'specific_resellers' | 'direct_only';
  marketingBadge?: string;
  /** Category id (packages.category_id) — matches a package-categories row; null when unassigned. */
  categoryId?: number | string | null;
  priceCount: number;
  subscriberCount: number;
  /** Resource limits enforced on the POS */
  limits: {
    locations: number | 'unlimited';
    users: number | 'unlimited';
    products: number | 'unlimited';
    invoices: number | 'unlimited';
    /** Invoice cap on the yearly plan (differs from the monthly cap). */
    invoicesYearly?: number | 'unlimited';
  };
  /** Modules enabled on this package — every tier includes the same set per the production catalog. */
  modules: string[];
};

const ALL_MODULES = [
  'CRM',
  'Essentials',
  'Manufacturing',
  'RMA',
  'Service',
  'Accounts',
  'E-commerce',
];

export const packages: Package[] = [
  {
    id: 'pkg_starter',
    internalCode: 'starter',
    name: 'Starter',
    tagline: 'Small type business packages',
    status: 'published',
    visibility: 'public',
    soldBy: 'all',
    priceCount: 2,
    subscriberCount: 248,
    limits: { locations: 1, users: 3, products: 1000, invoices: 1000 },
    modules: ALL_MODULES,
  },
  {
    id: 'pkg_basic',
    internalCode: 'basic',
    name: 'Basic',
    tagline: 'Small type business packages',
    status: 'published',
    visibility: 'public',
    soldBy: 'all',
    priceCount: 2,
    subscriberCount: 167,
    limits: { locations: 2, users: 6, products: 2000, invoices: 2500 },
    modules: ALL_MODULES,
  },
  {
    id: 'pkg_regular',
    internalCode: 'regular',
    name: 'Regular',
    tagline: 'Medium type business packages',
    status: 'published',
    visibility: 'public',
    soldBy: 'all',
    priceCount: 2,
    subscriberCount: 124,
    limits: { locations: 3, users: 10, products: 6500, invoices: 3000 },
    modules: ALL_MODULES,
  },
  {
    id: 'pkg_standard',
    internalCode: 'standard',
    name: 'Standard',
    tagline: 'Medium large type business packages',
    status: 'published',
    visibility: 'public',
    soldBy: 'all',
    priceCount: 2,
    subscriberCount: 96,
    limits: { locations: 6, users: 20, products: 'unlimited', invoices: 'unlimited' },
    modules: ALL_MODULES,
  },
  {
    id: 'pkg_enterprise',
    internalCode: 'enterprise',
    name: 'Enterprise',
    tagline: 'Large type business packages',
    status: 'published',
    visibility: 'public',
    soldBy: 'all',
    marketingBadge: 'Most popular',
    priceCount: 2,
    subscriberCount: 71,
    limits: { locations: 8, users: 30, products: 'unlimited', invoices: 'unlimited' },
    modules: ALL_MODULES,
  },
  {
    id: 'pkg_ultimate',
    internalCode: 'ultimate',
    name: 'Ultimate',
    tagline: 'Super shop types business packages',
    status: 'published',
    visibility: 'public',
    soldBy: 'all',
    priceCount: 2,
    subscriberCount: 42,
    limits: { locations: 10, users: 35, products: 'unlimited', invoices: 'unlimited' },
    modules: ALL_MODULES,
  },
  {
    id: 'pkg_extended',
    internalCode: 'extended',
    name: 'Extended',
    tagline: 'Special package for sum peoples',
    status: 'published',
    visibility: 'public',
    soldBy: 'all',
    priceCount: 2,
    subscriberCount: 18,
    limits: { locations: 15, users: 50, products: 'unlimited', invoices: 'unlimited' },
    modules: ALL_MODULES,
  },
];

export type Price = {
  id: string;
  packageId: string;
  currency: 'BDT' | 'USD';
  interval: 'month' | 'year' | 'one_time' | 'quarter';
  amount: number;
  trialDays: number;
  status: 'draft' | 'active' | 'deprecated' | 'archived';
  subscribersOn: number;
};

/**
 * Per-package monthly + yearly BDT prices. Yearly = monthly × 12 (no discount).
 * The numbers below mirror the production possignin.com pricing page.
 */
export const pricesByPackage: Record<string, Price[]> = {
  pkg_starter: [
    { id: 'pr_starter_m', packageId: 'pkg_starter', currency: 'BDT', interval: 'month', amount: 30000, trialDays: 7, status: 'active', subscribersOn: 198 },
    { id: 'pr_starter_y', packageId: 'pkg_starter', currency: 'BDT', interval: 'year', amount: 360000, trialDays: 7, status: 'active', subscribersOn: 50 },
  ],
  pkg_basic: [
    { id: 'pr_basic_m', packageId: 'pkg_basic', currency: 'BDT', interval: 'month', amount: 50000, trialDays: 7, status: 'active', subscribersOn: 132 },
    { id: 'pr_basic_y', packageId: 'pkg_basic', currency: 'BDT', interval: 'year', amount: 600000, trialDays: 7, status: 'active', subscribersOn: 35 },
  ],
  pkg_regular: [
    { id: 'pr_regular_m', packageId: 'pkg_regular', currency: 'BDT', interval: 'month', amount: 60000, trialDays: 7, status: 'active', subscribersOn: 96 },
    { id: 'pr_regular_y', packageId: 'pkg_regular', currency: 'BDT', interval: 'year', amount: 720000, trialDays: 7, status: 'active', subscribersOn: 28 },
  ],
  pkg_standard: [
    { id: 'pr_standard_m', packageId: 'pkg_standard', currency: 'BDT', interval: 'month', amount: 70000, trialDays: 7, status: 'active', subscribersOn: 78 },
    { id: 'pr_standard_y', packageId: 'pkg_standard', currency: 'BDT', interval: 'year', amount: 840000, trialDays: 7, status: 'active', subscribersOn: 18 },
  ],
  pkg_enterprise: [
    { id: 'pr_enterprise_m', packageId: 'pkg_enterprise', currency: 'BDT', interval: 'month', amount: 80000, trialDays: 7, status: 'active', subscribersOn: 58 },
    { id: 'pr_enterprise_y', packageId: 'pkg_enterprise', currency: 'BDT', interval: 'year', amount: 960000, trialDays: 7, status: 'active', subscribersOn: 13 },
  ],
  pkg_ultimate: [
    { id: 'pr_ultimate_m', packageId: 'pkg_ultimate', currency: 'BDT', interval: 'month', amount: 100000, trialDays: 7, status: 'active', subscribersOn: 34 },
    { id: 'pr_ultimate_y', packageId: 'pkg_ultimate', currency: 'BDT', interval: 'year', amount: 1200000, trialDays: 7, status: 'active', subscribersOn: 8 },
  ],
  pkg_extended: [
    { id: 'pr_extended_m', packageId: 'pkg_extended', currency: 'BDT', interval: 'month', amount: 150000, trialDays: 7, status: 'active', subscribersOn: 14 },
    { id: 'pr_extended_y', packageId: 'pkg_extended', currency: 'BDT', interval: 'year', amount: 1800000, trialDays: 7, status: 'active', subscribersOn: 4 },
  ],
};

/** Backwards-compat: the prices for the package the editor opens by default. */
export const proPrices: Price[] = pricesByPackage['pkg_enterprise']!;

export type Coupon = {
  id: string;
  code: string;
  internalName?: string;
  discountType: 'percent' | 'fixed' | 'trial_extension' | 'free_intervals';
  amountLabel: string;
  redemptionsUsed: number;
  redemptionsLimit: number | null;
  expiresAt: string | null;
  status: 'active' | 'expired' | 'draft' | 'disabled';
  resellerScoped: boolean;
};

export const coupons: Coupon[] = [
  { id: 'cp_1', code: 'EID2026', internalName: 'Eid 2026 promo', discountType: 'percent', amountLabel: '20% off 3mo', redemptionsUsed: 47, redemptionsLimit: 200, expiresAt: '2026-05-30', status: 'active', resellerScoped: false },
  { id: 'cp_2', code: 'NEWYEAR50', internalName: 'New Year flash', discountType: 'percent', amountLabel: '50% off 1mo', redemptionsUsed: 312, redemptionsLimit: null, expiresAt: null, status: 'active', resellerScoped: false },
  { id: 'cp_3', code: 'ACME-LAUNCH', internalName: 'Acme launch', discountType: 'fixed', amountLabel: '৳500 off 1x', redemptionsUsed: 12, redemptionsLimit: 50, expiresAt: '2026-06-30', status: 'active', resellerScoped: true },
  { id: 'cp_4', code: 'TRIAL-EXTEND', internalName: 'Trial extension', discountType: 'trial_extension', amountLabel: '+14d trial', redemptionsUsed: 88, redemptionsLimit: null, expiresAt: null, status: 'active', resellerScoped: false },
  { id: 'cp_5', code: 'EXPIRED-2025', internalName: 'Old promo', discountType: 'percent', amountLabel: '30% off', redemptionsUsed: 451, redemptionsLimit: 500, expiresAt: '2025-12-31', status: 'expired', resellerScoped: false },
];

export type Addon = {
  id: string;
  internalCode: string;
  name: string;
  description: string;
  unitLabel: string;
  amount: number;
  currency: 'BDT';
  billing: 'recurring' | 'one_time';
  status: 'active' | 'draft';
  inUseCount: number;
  /**
   * Which package tiers this add-on is sold against. Some make sense only
   * for capped tiers (e.g. extra products only for Starter/Basic/Regular —
   * Standard+ already has unlimited).
   */
  compatiblePackageIds: string[];
};

const ALL_PACKAGE_IDS = [
  'pkg_starter',
  'pkg_basic',
  'pkg_regular',
  'pkg_standard',
  'pkg_enterprise',
  'pkg_ultimate',
  'pkg_extended',
];

const CAPPED_PACKAGE_IDS = ['pkg_starter', 'pkg_basic', 'pkg_regular'];

export const addons: Addon[] = [
  {
    id: 'ao_extra_location',
    internalCode: 'extra_location',
    name: 'Extra location',
    description: 'Add another business location beyond your tier limit.',
    unitLabel: '/loc/mo',
    amount: 10000,
    currency: 'BDT',
    billing: 'recurring',
    status: 'active',
    inUseCount: 142,
    compatiblePackageIds: ALL_PACKAGE_IDS,
  },
  {
    id: 'ao_extra_user',
    internalCode: 'extra_user',
    name: 'Extra user',
    description: 'Add another POS user (cashier / manager) beyond your tier limit.',
    unitLabel: '/user/mo',
    amount: 5000,
    currency: 'BDT',
    billing: 'recurring',
    status: 'active',
    inUseCount: 89,
    compatiblePackageIds: ALL_PACKAGE_IDS,
  },
  {
    id: 'ao_extra_products_1k',
    internalCode: 'extra_products_1k',
    name: 'Extra products (1k)',
    description: 'Lift the product cap by 1,000. Only relevant on Starter, Basic, and Regular — Standard+ already has unlimited products.',
    unitLabel: '/1k/mo',
    amount: 20000,
    currency: 'BDT',
    billing: 'recurring',
    status: 'active',
    inUseCount: 23,
    compatiblePackageIds: CAPPED_PACKAGE_IDS,
  },
  {
    id: 'ao_extra_invoices_1k',
    internalCode: 'extra_invoices_1k',
    name: 'Extra invoices (1k)',
    description: 'Lift the invoice cap by 1,000 per month. Only relevant on Starter, Basic, and Regular.',
    unitLabel: '/1k/mo',
    amount: 15000,
    currency: 'BDT',
    billing: 'recurring',
    status: 'active',
    inUseCount: 17,
    compatiblePackageIds: CAPPED_PACKAGE_IDS,
  },
  {
    id: 'ao_dedicated_db',
    internalCode: 'dedicated_database',
    name: 'Dedicated database',
    description: 'Move off the shared MySQL into a private managed database. Better isolation + your own backup window.',
    unitLabel: '/mo flat',
    amount: 50000,
    currency: 'BDT',
    billing: 'recurring',
    status: 'active',
    inUseCount: 31,
    compatiblePackageIds: ALL_PACKAGE_IDS,
  },
  {
    id: 'ao_custom_domain',
    internalCode: 'custom_domain',
    name: 'Custom domain (CNAME)',
    description: 'Map your own domain (e.g. pos.your-shop.com) instead of the default *.itcitysoft.com subdomain.',
    unitLabel: '/mo flat',
    amount: 30000,
    currency: 'BDT',
    billing: 'recurring',
    status: 'active',
    inUseCount: 56,
    compatiblePackageIds: ALL_PACKAGE_IDS,
  },
  {
    id: 'ao_priority_support',
    internalCode: 'priority_support',
    name: 'Priority support',
    description: '4-hour response SLA on weekdays, dedicated WhatsApp line for urgent issues.',
    unitLabel: '/mo flat',
    amount: 100000,
    currency: 'BDT',
    billing: 'recurring',
    status: 'active',
    inUseCount: 8,
    compatiblePackageIds: ALL_PACKAGE_IDS,
  },
  {
    id: 'ao_sms_bundle',
    internalCode: 'sms_bundle_1k',
    name: 'SMS bundle (1,000)',
    description: 'Pre-pay 1,000 SMS sends — receipts, OTP, dunning, customer-loyalty messages.',
    unitLabel: '/1k SMS',
    amount: 80000,
    currency: 'BDT',
    billing: 'recurring',
    status: 'active',
    inUseCount: 12,
    compatiblePackageIds: ALL_PACKAGE_IDS,
  },
  {
    id: 'ao_backup_retention',
    internalCode: 'backup_retention_90d',
    name: 'Backup retention (90 d)',
    description: 'Extends point-in-time database backups from 7 days to 90 days.',
    unitLabel: '/mo flat',
    amount: 40000,
    currency: 'BDT',
    billing: 'recurring',
    status: 'active',
    inUseCount: 9,
    compatiblePackageIds: ALL_PACKAGE_IDS,
  },
  {
    id: 'ao_onboarding',
    internalCode: 'onboarding',
    name: 'Onboarding (one-time)',
    description: 'A 2-hour live setup session: products import, tax/VAT config, printer + barcode scanner, first sale, staff training.',
    unitLabel: 'once',
    amount: 500000,
    currency: 'BDT',
    billing: 'one_time',
    status: 'active',
    inUseCount: 12,
    compatiblePackageIds: ALL_PACKAGE_IDS,
  },
  {
    id: 'ao_data_migration',
    internalCode: 'data_migration',
    name: 'Data migration (one-time)',
    description: 'We import your existing products, customers, and historical sales from Excel/CSV/another POS into Possignin.',
    unitLabel: 'once',
    amount: 800000,
    currency: 'BDT',
    billing: 'one_time',
    status: 'active',
    inUseCount: 6,
    compatiblePackageIds: ALL_PACKAGE_IDS,
  },
];

/**
 * Pricing page data for marketing/signup views. Yearly = monthly × 12 per spec.
 * Mirrors the 7 tiers shown on possignin.com.
 */
export const pricingPagePackages: Array<{
  id: string;
  name: string;
  priceMonthlyMinor: number | null;
  priceYearlyMinor: number | null;
  tagline: string;
  features: string[];
  /** Invoice caps per interval (null = unlimited); the /pricing toggle picks one. */
  invoicesMonthly?: number | null;
  invoicesYearly?: number | null;
  badge?: string;
  cta: string;
  ctaHref: string;
}> = [
  {
    id: 'pkg_starter',
    name: 'Starter',
    priceMonthlyMinor: 30000,
    priceYearlyMinor: 360000,
    tagline: 'Small type business packages',
    features: ['1 business location', '3 users', '1,000 products', '1,000 invoices', ...ALL_MODULES.map((m) => `${m} module`)],
    cta: 'Try free',
    ctaHref: '/signup?plan=starter',
  },
  {
    id: 'pkg_basic',
    name: 'Basic',
    priceMonthlyMinor: 50000,
    priceYearlyMinor: 600000,
    tagline: 'Small type business packages',
    features: ['2 business locations', '6 users', '2,000 products', '2,500 invoices', ...ALL_MODULES.map((m) => `${m} module`)],
    cta: 'Try free',
    ctaHref: '/signup?plan=basic',
  },
  {
    id: 'pkg_regular',
    name: 'Regular',
    priceMonthlyMinor: 60000,
    priceYearlyMinor: 720000,
    tagline: 'Medium type business packages',
    features: ['3 business locations', '10 users', '6,500 products', '3,000 invoices', ...ALL_MODULES.map((m) => `${m} module`)],
    cta: 'Try free',
    ctaHref: '/signup?plan=regular',
  },
  {
    id: 'pkg_standard',
    name: 'Standard',
    priceMonthlyMinor: 70000,
    priceYearlyMinor: 840000,
    tagline: 'Medium large type business packages',
    features: ['6 business locations', '20 users', 'Unlimited products', 'Unlimited invoices', ...ALL_MODULES.map((m) => `${m} module`)],
    cta: 'Try free',
    ctaHref: '/signup?plan=standard',
  },
  {
    id: 'pkg_enterprise',
    name: 'Enterprise',
    priceMonthlyMinor: 80000,
    priceYearlyMinor: 960000,
    tagline: 'Large type business packages',
    features: ['8 business locations', '30 users', 'Unlimited products', 'Unlimited invoices', ...ALL_MODULES.map((m) => `${m} module`)],
    badge: 'Most popular',
    cta: 'Try free',
    ctaHref: '/signup?plan=enterprise',
  },
  {
    id: 'pkg_ultimate',
    name: 'Ultimate',
    priceMonthlyMinor: 100000,
    priceYearlyMinor: 1200000,
    tagline: 'Super shop types business packages',
    features: ['10 business locations', '35 users', 'Unlimited products', 'Unlimited invoices', ...ALL_MODULES.map((m) => `${m} module`)],
    cta: 'Try free',
    ctaHref: '/signup?plan=ultimate',
  },
  {
    id: 'pkg_extended',
    name: 'Extended',
    priceMonthlyMinor: 150000,
    priceYearlyMinor: 1800000,
    tagline: 'Special package for sum peoples',
    features: ['15 business locations', '50 users', 'Unlimited products', 'Unlimited invoices', ...ALL_MODULES.map((m) => `${m} module`)],
    cta: 'Try free',
    ctaHref: '/signup?plan=extended',
  },
];
